/**
 * ExitConfirmDialog Component
 * 
 * Confirmation dialog shown when user tries to leave during an active session.
 * Offers options to save progress, exit without saving, or cancel.
 */

import { useTranslation } from 'react-i18next';

interface ExitConfirmDialogProps {
  onSave: () => Promise<void>;
  onExitWithoutSaving: () => void;
  onCancel: () => void;
  isSaving?: boolean;
}

export default function ExitConfirmDialog({
  onSave,
  onExitWithoutSaving,
  onCancel,
  isSaving = false,
}: ExitConfirmDialogProps) {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 pointer-events-auto">
      <div className="text-center px-4 max-w-sm w-full">
        <div className="bg-gray-800/95 backdrop-blur-md rounded-2xl px-6 py-6 border border-white/10 shadow-2xl">
          {/* Icon */}
          <div className="text-4xl mb-3">💬</div>
          
          {/* Title */}
          <h2 className="text-xl font-bold text-white mb-2">
            {t('scenario.session.exitConfirm.title', 'Sair da conversa?')}
          </h2>
          
          {/* Description */}
          <p className="text-white/70 text-sm mb-6">
            {t('scenario.session.exitConfirm.description', 'Queres guardar o teu progresso antes de sair?')}
          </p>
          
          {/* Actions */}
          <div className="space-y-2.5">
            <button
              onClick={onSave}
              disabled={isSaving}
              className="w-full px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-green-800 disabled:opacity-60 text-white font-semibold rounded-xl transition-colors"
            >
              {isSaving
                ? t('scenario.session.saving', 'A guardar...')
                : t('scenario.session.saveAndExit', 'Guardar e Sair')}
            </button>
            
            <button
              onClick={onExitWithoutSaving}
              disabled={isSaving}
              className="w-full px-6 py-2.5 bg-red-600/80 hover:bg-red-600 disabled:opacity-60 text-white font-medium rounded-xl transition-colors"
            >
              {t('scenario.session.exitConfirm.exitWithoutSaving', 'Sair sem guardar')}
            </button>
            
            <button
              onClick={onCancel}
              disabled={isSaving}
              className="w-full px-6 py-2.5 bg-white/10 hover:bg-white/15 disabled:opacity-60 text-white/80 font-medium rounded-xl border border-white/10 transition-colors"
            >
              {t('common.buttons.cancel', 'Cancelar')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
